/**
 * オフラインステータスバー
 * ネットワーク状態と保留中の決済を表示
 */
import React, { useState } from 'react';
import {
  Box,
  Alert,
  AlertTitle,
  IconButton,
  Collapse,
  Typography,
  Button,
  Badge,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudOff as CloudOffIcon,
  Cloud as CloudIcon,
  Sync as SyncIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useOfflineQueue } from '../../contexts/OfflineQueueContext';

const OfflineStatusBar = () => {
  const {
    isOnline,
    queueSummary,
    isSyncing,
    lastSyncTime,
    syncNow,
    getAllPayments,
  } = useOfflineQueue();

  const [expanded, setExpanded] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [pendingPayments, setPendingPayments] = useState([]);

  const handleViewDetails = async () => {
    const payments = await getAllPayments();
    setPendingPayments(payments);
    setDetailsOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ja-JP');
  };

  // オンラインで保留中の決済がない場合は何も表示しない
  if (isOnline && queueSummary.pending === 0) {
    return null;
  }

  return (
    <>
      <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200 }}>
        {/* オフラインアラート */}
        {!isOnline && (
          <Alert
            severity="warning"
            icon={<CloudOffIcon />}
            sx={{
              borderRadius: 0,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <AlertTitle>オフラインモード</AlertTitle>
                <Typography variant="body2">
                  決済データはローカルに保存され、接続回復時に自動同期されます
                </Typography>
              </Box>
              <Badge badgeContent={queueSummary.pending} color="error">
                <Chip
                  icon={<WarningIcon />}
                  label={`保留中: ${formatCurrency(queueSummary.pendingAmount)}`}
                  color="warning"
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                />
              </Badge>
            </Box>
          </Alert>
        )}

        {/* 保留中の決済がある場合（オンライン時） */}
        {isOnline && queueSummary.pending > 0 && (
          <Alert
            severity="info"
            icon={isSyncing ? <SyncIcon className="rotating" /> : <CloudIcon />}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={syncNow}
                disabled={isSyncing}
                startIcon={<SyncIcon />}
              >
                今すぐ同期
              </Button>
            }
            sx={{ borderRadius: 0 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2">
                保留中の決済: {queueSummary.pending}件 ({formatCurrency(queueSummary.pendingAmount)})
              </Typography>
              {isSyncing && <LinearProgress sx={{ width: 100 }} />}
              <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Alert>
        )}

        {/* 展開時の詳細 */}
        <Collapse in={expanded}>
          <Box sx={{ bgcolor: 'background.paper', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">
                キュー状態
              </Typography>
              <Button size="small" onClick={handleViewDetails}>
                詳細を表示
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Chip label={`保留中: ${queueSummary.pending}`} color="warning" size="small" />
              <Chip label={`同期済: ${queueSummary.synced}`} color="success" size="small" />
              <Chip label={`失敗: ${queueSummary.failed}`} color="error" size="small" />
            </Box>
            {lastSyncTime && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                最終同期: {formatDate(lastSyncTime)}
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* 詳細ダイアログ */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>オフラインキュー詳細</DialogTitle>
        <DialogContent>
          {pendingPayments.length === 0 ? (
            <Typography color="text.secondary">保留中の決済はありません</Typography>
          ) : (
            <List>
              {pendingPayments.map((payment) => (
                <ListItem key={payment.id} divider>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="body1">
                          {formatCurrency(payment.total || payment.amount)}
                        </Typography>
                        <Chip
                          size="small"
                          label={payment.status}
                          color={
                            payment.status === 'synced' ? 'success' :
                            payment.status === 'failed' ? 'error' : 'warning'
                          }
                        />
                        {payment.paymentMethodName && (
                          <Chip size="small" label={payment.paymentMethodName} variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" component="span">
                          作成: {formatDate(payment.createdAt)}
                        </Typography>
                        {payment.lastError && (
                          <Typography variant="caption" color="error" component="div">
                            エラー: {payment.lastError}
                          </Typography>
                        )}
                        {payment.retryCount > 0 && (
                          <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                            リトライ: {payment.retryCount}回
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>閉じる</Button>
          <Button onClick={syncNow} disabled={isSyncing} startIcon={<SyncIcon />}>
            今すぐ同期
          </Button>
        </DialogActions>
      </Dialog>

      {/* 回転アニメーション用CSS（prefers-reduced-motion対応） */}
      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .rotating {
            animation: none;
          }
        }
      `}</style>
    </>
  );
};

export default OfflineStatusBar;
